import { Injectable, inject } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Subject, timer } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ComponentsToLoad } from '../enums/components-to-load.enum';
import { AuthKeys } from '../enums/local-storage-keys.enum';
import { RefreshResponse } from '../models/auth.model';
import { ConsumerPortalConfig } from '../models/consumer-portal-config';
import { ErrorType } from '../models/error.model';
import { LoginSession } from '../models/login-session.model';
import { clearContractinStore } from '../store/domains/contract/contract.actions';
import { cleanAccountSummary } from '../store/domains/customer-summary/customer-summary.actions';
import { clearInvoicesinStore } from '../store/domains/invoices/invoices.actions';
import { clearItemsOut } from '../store/domains/items-out-actions/items-out-action.actions';
import { AppFacadeService } from './app-facade.service';
import { AppMediatorService } from './app-mediator.service';
import { ConsumerPortalApiService } from './consumer-portal-api.service';
import { ErrorService } from './error.service';
import { VersionToggleService } from './version-toggle';
import { cleanAccountFeature } from '../store/domains/feature-account/account.actions';

@Injectable({
    providedIn: 'root'
})
export class AuthUiService {
    private readonly store = inject(Store);
    private readonly errorService = inject(ErrorService);
    private readonly consumerPortalApiService = inject(ConsumerPortalApiService);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly versionToggle = inject(VersionToggleService);

    defaultPageSize!: number;
    accessToken = '';
    xApiKey = '';

    private refreshAuthTimer: Observable<number> = new Observable<number>();
    private authData?: LoginSession;
    private readonly _clearRefreshAuthTimer = new Subject();
    // Always use CONSTANT_CASE
    // eslint-disable-next-line @typescript-eslint/naming-convention
    private readonly REFRESH_TOKEN_TIMER_MS = 1500000;

    get user(): LoginSession {
        return this.appMediatorService.localStorageService.getCurrentUser?.Auth as LoginSession;
    }

    initAuth() {
        if (this.user) {
            this.appFacadeService.setAuthenticated(false);
        }
    }

    logout() {
        [AuthKeys.AcceessToken, AuthKeys.User, AuthKeys.RefreshToken].map((key: string) => {
            this.appMediatorService.localStorageService.removeItem(key, this.appFacadeService.versionToggleService.isConsumerQuickLinkVersion());
        });
        [clearContractinStore, cleanAccountSummary, clearInvoicesinStore, clearItemsOut, cleanAccountFeature].map((action: unknown) => {
            if (typeof action === 'function') {
                this.store.dispatch(action());
            } else {
                this.store.dispatch(action as Action);
            }
        });
        /**
         * Reset/Clear Selected Contracts on logout
         */
        this.appFacadeService.setSelectedContracts([]);

        this.returnToLogin();
    }

    returnToLogin() {
        // It's important to first route to logout page for Quick Link Version to return false in case of logout
        // becasuse Quick Link Version is determined by the page URL
        this.appFacadeService.updateLocation(ComponentsToLoad.Logout);
        this.clearRefreshAuthTimer();
        this.appFacadeService.setAuthenticated(false);
    }

    activateRefreshAuthTimer(data: LoginSession) {
        this.clearRefreshAuthTimer();
        // TODO: Remove the need of authData
        this.authData = data;
        // Only set startDue as zero(0) if this is the first time the user logs in
        // because first time a token is exist and it is valid for 5 minutes.
        const startDue = !this.authData.initialLogin ? 0 : this.REFRESH_TOKEN_TIMER_MS;
        // run after x mins and then every x minutes
        this.refreshAuthTimer = timer(startDue, this.REFRESH_TOKEN_TIMER_MS).pipe(
            switchMap(i => this.getRefreshToken(this.authData).pipe(map(() => i))),
            takeUntil(this._clearRefreshAuthTimer)
        );

        return this.refreshAuthTimer;
    }

    clearRefreshAuthTimer() {
        this._clearRefreshAuthTimer.complete();
    }

    private getRefreshToken(data?: LoginSession) {
        return this.consumerPortalApiService
            .post({
                controller: 'auth/tokens/refresh',
                method: 'POST',
                body: this.authData
            })
            .pipe(
                map((response: unknown) => response as RefreshResponse),
                tap({
                    next: (response: RefreshResponse) => {
                        if (response) {
                            if (this.authData) {
                                this.authData.RefreshToken = response?.refreshToken ?? undefined;
                                this.authData.accessToken = response?.accessToken ?? undefined;
                                this.authData.AccessToken = response?.accessToken ?? undefined;
                            }

                            const userDetails: ConsumerPortalConfig = {
                                ...(this.appMediatorService.localStorageService.getCurrentUser as ConsumerPortalConfig),
                                organizationType: response?.organizationType ?? '',
                                /* eslint-disable @typescript-eslint/naming-convention */
                                /**
                                 * Note camelCase : DB Model
                                 */
                                Auth: {
                                    ...this.user,
                                    accessToken: response?.accessToken,
                                    RefreshToken: response?.refreshToken
                                }
                            };
                            this.appMediatorService.localStorageService.persistUser(AuthKeys.User, userDetails);
                            if (!data?.initialLogin && userDetails.Auth?.accessToken) {
                                this.appFacadeService.setAuthenticated(true);
                            }
                            if (data?.isAdmin && userDetails.Auth?.accessToken) {
                                this.appFacadeService.setAdmin(true);
                                this.appFacadeService.setAdminRoles(response?.Role);
                            }
                        }
                    },
                    error: error => {
                        // BackendInterceptor owns refresh auth failures, with exactly one gap, and
                        // this handler covers that gap and nothing else.
                        //
                        // The gap: handleError rewrites an online status 0 to 502
                        // (`navigator.onLine && error.status === 0`) BEFORE its auth check, and 502
                        // is an SQS error, so an online refresh timeout — one that reaches the
                        // browser with no CORS headers — never reaches the interceptor's logout
                        // branch. It only shows the gateway dialog.
                        //
                        // Deliberately NO 401/403/404 arm. The interceptor handles 401/403 and then
                        // rethrows, so a second handler here sent the host a duplicate AuthFailed
                        // event and ran logout() twice; 404 is unreachable (the refresh proxy throws
                        // UnauthorizedException only).
                        //
                        // The navigator.onLine test mirrors the interceptor's rewrite and is load-
                        // bearing: the interceptor mutates its own copy of the error and rethrows the
                        // original, so `error.status` is 0 here whether online or offline. Offline the
                        // interceptor's own refresh branch fires, so this must not. **If handleError's
                        // status normalisation changes, change this with it** — nothing will fail to
                        // signal the drift. (ADO-120329)
                        const isOnlineRefreshTimeout = navigator.onLine && !error?.status && error?.url?.includes('tokens/refresh');

                        if (isOnlineRefreshTimeout && !this.versionToggle.isQuickLinkVersion()) {
                            this.errorService.setError(ErrorType.AuthFailed);
                            this.logout();
                        }
                    }
                })
            );
    }
}
